import {useEffect, useMemo, useState} from "react";
import pxToRem from "hooks/usePxToRem";

import {Stack, Typography} from "@mui/material";

import {CustomButton} from "ui";
import {ConfirmModal, DefaultCardStructure} from "components";
import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {
  DeleteLogisticBillLadingLoad,
  PatchProcessById,
} from "services/api_v2/Processes/Processes.service";
import useAlertMessage from "hooks/useAlertMessage";
import useToast from "hooks/useToast";
import {Item} from "./components";
import BulkInsertionModal from "components/BulkInsertionModal";
import {calcConsideredWeight} from "../../utils/calculateConsideredWeight";
import {buildLogisticLoadsPayload} from "../../utils/loadPayload";
import {CloneCargoItemsOptions, CloneItem} from "types/ComponentsTypes";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {LogisticBillLadingLoads} from "views/ProcessDetails/refactor/types/knowledges.types";
import {Modality} from "util/globalEnums";
import {useResolvedProcessId} from "views/ProcessDetails/refactor/contexts/ResolvedProcessIdContext";

type Props = {
  loadIndex: number;
};

const CargoItems = (props: Props) => {
  const {loadIndex} = props;
  const {getValues, reset, formState, setValue, setError} =
    useFormContext<Process>();
  const [showToast] = useToast();
  const [showAlert] = useAlertMessage();
  const processId = useResolvedProcessId();

  const initialCargoItem = {
    number: null,
    seal: "",
    equipment: null,
    quantity: 0,
    netWeight: 0,
    grossWeight: 0,
    measurement: 0,
    tare: 0,
    typeItemLoad: "",
    barSeal: "",
    typePackages: null,
    packageContainer: null,
    dangerousGoodsClass: "",
    dangerousGoodsCode: "",
  };

  const cancelledProcess = getValues("agencyingSituation") === "Cancelado";
  const houseId = useWatch({name: "houseId"});
  const equipments = useWatch({name: "logisticEquipments"});

  const indexOfHouse = getValues("knowledges")?.findIndex(
    (item) => item.typeKnowledge === "House",
  );
  const rootName =
    `knowledges[${indexOfHouse}].logisticBillLading.logisticBillLadingLoads` as `knowledges.0.logisticBillLading.logisticBillLadingLoads`;

  const {fields, append, insert, remove, update} = useFieldArray({
    name: rootName,
    keyName: "idForm",
  });
  const logisticBillLadingLoads = useWatch({name: rootName});
  const totalOfEquipments = () => {
    const total = equipments?.reduce((acc, curr) => acc + curr?.quantity, 0);
    return total;
  };

  const handleAppendNewItem = () => {
    if (getValues(`processLoads.${loadIndex}.typeLoad`) === "LCL") {
      append(initialCargoItem);
      return;
    }
    const totalOfCargoItems = getValues(rootName)?.length ?? 0;
    if (totalOfCargoItems >= totalOfEquipments()) {
      showToast(
        "O número de equipamentos é igual ou maior ao número de itens",
        "warning",
      );
    } else {
      append(initialCargoItem);
    }
  };

  const setHouseMeasurements = (shouldDirtyFields = true) => {
    const cargoItems = getValues(rootName) ?? [];
    const totalGrossWeight = cargoItems.reduce(
      (acc, prev) => acc + prev.grossWeight,
      0,
    );
    setValue(
      `processLoads[${loadIndex}].grossWeight` as `processLoads.0.grossWeight`,
      totalGrossWeight,
      {
        shouldDirty: shouldDirtyFields,
      },
    );
    const totalMeasurements = cargoItems.reduce(
      (acc, prev) => acc + prev.measurement,
      0,
    );
    setValue(
      `processLoads[${loadIndex}].cubicMeters` as `processLoads.0.cubicMeters`,
      totalMeasurements,
      {
        shouldDirty: shouldDirtyFields,
      },
    );

    const totalQuantities = cargoItems.reduce(
      (acc, prev) => acc + prev.quantity,
      0,
    );

    setValue(
      `processLoads[${loadIndex}].quantities` as `processLoads.0.quantities`,
      totalQuantities,
      {
        shouldDirty: shouldDirtyFields,
      },
    );

    const loadData = calcConsideredWeight(
      totalMeasurements ?? 0,
      totalGrossWeight ?? 0,
    );
    setValue(
      `processLoads[${loadIndex}].consideredWeight` as `processLoads.0.consideredWeight`,
      loadData.consideredWeight,
      {shouldDirty: shouldDirtyFields},
    );
    setValue(
      `processLoads[${loadIndex}].cubeWeight` as `processLoads.0.cubeWeight`,
      loadData.cubeWeight,
      {
        shouldDirty: shouldDirtyFields,
      },
    );
  };

  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    itemId?: number;
    index?: number;
  }>({});

  /**
   * Não vamos mais deletar itens de carga, uma vez que já trazemos a quantidade
   * correta conforme quantidade de equipamentos. O botão de apagar foi substituído
   * por um botão para limpar os campos.
   */

  const handleDeleteLogisticBillLading = async (field: any, index: number) => {
    if (!field.id) {
      remove(index);
      return;
    }

    setItemToDelete({itemId: field.id as number, index: index});
    setConfirmModalIsOpen(true);
  };

  const handleClose = () => {
    setConfirmModalIsOpen(false);
  };
  const confirmDeletionPackage = async () => {
    if (!itemToDelete?.itemId || itemToDelete?.index === undefined) {
      return;
    }
    try {
      await DeleteLogisticBillLadingLoad(itemToDelete.itemId);
      remove(itemToDelete.index);

      const currentValues = getValues();
      const newBillLadingLoads =
        currentValues?.knowledges?.[indexOfHouse ?? 0]?.logisticBillLading
          ?.logisticBillLadingLoads;
      const newKnowledges = [...(currentValues.knowledges ?? [])];

      newKnowledges[indexOfHouse!] = {
        ...newKnowledges[indexOfHouse!],
        logisticBillLading: {
          ...newKnowledges[indexOfHouse!]?.logisticBillLading,
          logisticBillLadingLoads: newBillLadingLoads,
        },
      };

      reset({
        ...currentValues,
        knowledges: newKnowledges,
      });
      handleClose();
      setHouseMeasurements(false);
      const currentLoad = getValues(`processLoads.${loadIndex}` as const);

      await PatchProcessById(processId, buildLogisticLoadsPayload(currentLoad));

      showToast("Item removido com sucesso", "success");
    } catch (e: any) {
      console.error(e);
      showAlert(e?.response?.data?.message);
    }
  };

  const getDefaultItemValues = (): CloneCargoItemsOptions[] => {
    const defaultItemValues = fields.map(() => ({
      icon: CloneItem.content_copy,
    }));
    return defaultItemValues;
  };

  const [cloningItemValues, setCloningItemValues] = useState<any>(null);

  const [cloneItemConfig, setCloneItemConfig] = useState<
    CloneCargoItemsOptions[]
  >(getDefaultItemValues());

  const handleClickOnCloneItem = (index: number, valuesToClone: any) => {
    const cloneItems = structuredClone(cloneItemConfig);

    if (cloneItems[index].icon === CloneItem.close) {
      setCloningItemValues(null);
      cloneItems.forEach((item) => (item.icon = CloneItem.content_copy));
      setCloneItemConfig(cloneItems);
      showToast("Clonagem encerrada!");
      setIsCloningItem(false);
      return;
    }

    if (cloneItems[index].icon === CloneItem.content_paste) {
      const dataToClone = structuredClone(cloningItemValues);

      delete dataToClone?.seal;
      delete dataToClone?.barSeal;
      delete dataToClone?.id;
      delete dataToClone?.number;

      update(index, {
        id: getValues(rootName)?.[index]?.id,
        ...dataToClone,
      });
      setHouseMeasurements();
      showToast("Dados clonados!", "warning");

      return;
    }

    if (cloneItems[index].icon === CloneItem.content_copy) {
      setCloningItemValues(valuesToClone);
      cloneItems.forEach((item, forIndex) => {
        return forIndex === index
          ? (item.icon = CloneItem.close)
          : (item.icon = CloneItem.content_paste);
      });
      showToast("Item copiado!", "success");

      setCloneItemConfig(cloneItems);

      return;
    }
  };

  const [isCloningItem, setIsCloningItem] = useState(false);

  const [bulkInsertionModalIsOpen, setBulkInsertionModalIsOpen] =
    useState(false);

  const [isBulkInsertionConfirmed, setIsBulkInsertionConfirmed] =
    useState(false);

  const [logisticBillLadingLoadsSnapshot, setLogisticBillLadingLoadsSnapshot] =
    useState<LogisticBillLadingLoads[] | null>(null);

  const handleOpenBulkInsertionModal = () => {
    setImportedItemsIndexes([]);
    setIsBulkInsertionConfirmed(false);
    setLogisticBillLadingLoadsSnapshot(
      structuredClone(getValues(rootName) ?? []),
    );
    setBulkInsertionModalIsOpen(true);
  };

  const [importedItemsIndexes, setImportedItemsIndexes] = useState<number[]>(
    [],
  );

  const rollbackBulkInsertion = () => {
    if (!logisticBillLadingLoadsSnapshot) {
      return;
    }

    setValue(rootName, logisticBillLadingLoadsSnapshot, {shouldDirty: true});
    setHouseMeasurements();
  };

  const applyImportedItems = useMemo(
    () => (items: Record<string, unknown>[], maxIndexToReplace: number) => {
      const currentItems = getValues(rootName) ?? [];
      const idsToPreserve = Array.from({length: maxIndexToReplace}, (_, i) =>
        currentItems?.[i]?.id ? currentItems[i].id : undefined,
      );

      const indexesToRemove = Array.from(
        {length: maxIndexToReplace},
        (_, i) => i,
      );
      remove(indexesToRemove);
      for (let i = 0; i < maxIndexToReplace; i++) {
        const nextItem = items[i] as unknown as LogisticBillLadingLoads;

        insert(i, {
          ...items[i],
          id: idsToPreserve[i],
          dateGateIn: currentItems?.[i]?.dateGateIn ?? null,
          dateGateOut: currentItems?.[i]?.dateGateOut ?? null,
        } as unknown as LogisticBillLadingLoads);
        setValue(`${rootName}.${i}.equipment`, nextItem?.equipment ?? null, {
          shouldDirty: true,
        });
        setValue(
          `${rootName}.${i}.packageContainer`,
          nextItem?.packageContainer ?? null,
          {shouldDirty: true},
        );
      }
    },
    [getValues, insert, remove, rootName],
  );

  const handleCloseBulkInsertionModal = () => {
    if (!isBulkInsertionConfirmed) {
      rollbackBulkInsertion();
    }
    setImportedItemsIndexes([]);
    setBulkInsertionModalIsOpen(false);
  };

  const validateNumberOfItems = () => {
    const cargoItems = getValues(rootName) ?? [];

    const dirtyLogisticBillLading =
      formState.dirtyFields?.knowledges?.[indexOfHouse ?? 0]
        ?.logisticBillLading;

    const dirtyItems =
      dirtyLogisticBillLading &&
      typeof dirtyLogisticBillLading === "object" &&
      "logisticBillLadingLoads" in dirtyLogisticBillLading
        ? (dirtyLogisticBillLading.logisticBillLadingLoads ?? [])
        : [];

    const countItemsWithIdOrDirty = cargoItems.filter((item, index) => {
      const hasId = Boolean(item?.id);
      // dirty item = existe ao menos um campo true dentro do objeto dirty daquele índice
      const dirtyMap = dirtyItems?.[index];
      const hasDirtyField = Boolean(
        dirtyMap &&
        Object.values(dirtyMap as Record<string, boolean>).some(Boolean),
      );
      return hasId || hasDirtyField;
    }).length;
    const totalEquipments = totalOfEquipments() ?? 0;

    if (countItemsWithIdOrDirty > totalEquipments) {
      showToast(
        "A soma de itens existentes/alterados excede a quantidade de equipamentos.",
        "error",
      );
    }
  };

  const onConfirmBulkInsertion = () => {
    setIsBulkInsertionConfirmed(true);
    setHouseMeasurements();
    showToast("Itens de carga importados com sucesso!", "success");
    setBulkInsertionModalIsOpen(false);
    validateNumberOfItems();
  };
  useEffect(() => {
    setCloneItemConfig(getDefaultItemValues());
  }, [logisticBillLadingLoads?.length]);

  return (
    <>
      {confirmModalIsOpen && (
        <ConfirmModal
          isOpen={confirmModalIsOpen}
          title={"Deletar item de carga"}
          subtitle={`Esta ação não pode ser revertida!`}
          confirmButtonFn={() => confirmDeletionPackage()}
          onClose={handleClose}
        />
      )}
      {bulkInsertionModalIsOpen && (
        <BulkInsertionModal
          isOpen={bulkInsertionModalIsOpen}
          onClose={handleCloseBulkInsertionModal}
          slotsCount={fields.length}
          renderItem={(index) => {
            const field = fields[index];
            if (!field) return null;
            return (
              <Item
                key={field?.idForm ?? `${index}-${field?.idForm ?? "item"}`}
                field={field}
                index={index}
                loadIndex={props.loadIndex}
                handleDeleteLogisticBillLading={handleDeleteLogisticBillLading}
                setHouseMeasurements={setHouseMeasurements}
                cloneItemConfig={cloneItemConfig[index]}
                handleClickOnCloneItem={handleClickOnCloneItem}
                isCloningItem={isCloningItem}
                rootName={rootName}
              />
            );
          }}
          setHouseMeasurements={setHouseMeasurements}
          setIsBulkInsertionConfirmed={setIsBulkInsertionConfirmed}
          onConfirmBulkInsertion={onConfirmBulkInsertion}
          importedItemsIndexes={importedItemsIndexes}
          setImportedItemsIndexes={setImportedItemsIndexes}
          applyImportedItems={applyImportedItems}
        />
      )}
      <DefaultCardStructure
        title="Itens Da Carga"
        hasDivider={true}
        isSubcard={false}
        button={
          <>
            <CustomButton
              variant="outlined"
              startIcon="android"
              onClickFn={handleOpenBulkInsertionModal}
              disabled={
                cancelledProcess || getValues("modality") === Modality.Aérea
              }
            >
              Adicionar vários
            </CustomButton>
            <CustomButton
              onClickFn={() => {
                if (isCloningItem) {
                  setCloningItemValues(null);
                  setCloneItemConfig(getDefaultItemValues());
                }
                setIsCloningItem((prev) => !prev);
              }}
              variant="text"
            >
              {isCloningItem ? `Finalizar clonagem` : `Clonar Item`}
            </CustomButton>
            <CustomButton
              startIcon="add"
              onClickFn={handleAppendNewItem}
              disabled={
                !houseId ||
                (getValues("logisticEquipments")?.length === 0 &&
                  getValues("processLoads.0.typeLoad") === "FCL") ||
                cancelledProcess ||
                getValues("modality") === Modality.Aérea
              }
            >
              Incluir item
            </CustomButton>
          </>
        }
      >
        <Stack sx={{gap: pxToRem(8)}}>
          {fields.length === 0 && (
            <Typography>Não existem itens de carga no processo.</Typography>
          )}
          {fields.map((field: any, index) => (
            <Item
              key={field?.idForm}
              field={field}
              index={index}
              loadIndex={props.loadIndex}
              handleDeleteLogisticBillLading={handleDeleteLogisticBillLading}
              setHouseMeasurements={setHouseMeasurements}
              cloneItemConfig={cloneItemConfig[index]}
              handleClickOnCloneItem={handleClickOnCloneItem}
              isCloningItem={isCloningItem}
              rootName={rootName}
            />
          ))}
        </Stack>
      </DefaultCardStructure>
    </>
  );
};

export default CargoItems;
